import React, { type JSX, type RefObject } from "react";

import AccessibleButton, { type ButtonProps } from "../../components/views/elements/AccessibleButton";
import { useRovingTabIndex } from "../RovingTabIndex";

type Props<T extends keyof HTMLElementTagNameMap> = Omit<ButtonProps<T>, "tabIndex"> & {
    inputRef?: RefObject<HTMLElementTagNameMap[T] | null>;
    focusOnMouseOver?: boolean;
};

// Wrapper to allow use of useRovingTabIndex for simple AccessibleButtons outside of React Functional Components.
export const RovingAccessibleButton = <T extends keyof HTMLElementTagNameMap>({
    inputRef,
    onFocus,
    onMouseOver,
    focusOnMouseOver,
    ...props
}: Props<T>): JSX.Element => {
    const [onFocusInternal, isActive, ref] = useRovingTabIndex<HTMLElementTagNameMap[T]>(inputRef);
    return (
        <AccessibleButton
            {...props}
            onFocus={(event: React.FocusEvent<never, never>) => {
                onFocusInternal();
                onFocus?.(event);
            }}
            onMouseOver={(event: React.MouseEvent<never, never>) => {
                if (focusOnMouseOver) onFocusInternal();
                onMouseOver?.(event);
            }}
            ref={ref}
            tabIndex={isActive ? 0 : -1}
        />
    );
};
